/**
 * Back/forward-cache (bfcache) restore guard.
 *
 * When the browser restores a page from bfcache (e.g. the user hits Back into
 * the app after logging out on the auth domain), React does NOT remount, so the
 * on-mount session validation in CommonAuthContext never re-runs. A page frozen
 * while logged in can therefore come back looking authenticated after the
 * session was actually killed (logout, idle timeout, admin revoke, another
 * device).
 *
 * This installs a single `pageshow` listener that, ONLY on a bfcache restore
 * (`event.persisted`), asks the server whether the session is still valid and
 * redirects to login if not. Paint stays instant — the page is shown from
 * bfcache and the check runs asynchronously — so a still-valid session keeps
 * the snappy back/forward restore; only a dead session triggers a redirect.
 *
 * This is the authoritative replacement for the old `logout_in_progress` /
 * `force_logout` sticky flags: instead of guessing from client state, it asks
 * the source of truth on the one event (bfcache restore) the contexts miss.
 *
 * @param {() => Promise<boolean>} isSessionValid - resolves true when the
 *   server still considers the session valid. Should fetch with `cache:
 *   'no-store'` so a restored page never reads a cached 200.
 * @param {() => void} onInvalid - navigate to login (e.g. redirectToLogin()).
 */
export function installBfcacheGuard(isSessionValid, onInvalid) {
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted) return; // only act on bfcache restores
    try {
      if (!(await isSessionValid())) onInvalid();
    } catch {
      // Network error while re-validating a restored page → safest to re-auth.
      onInvalid();
    }
  });
}
